/**
 * @fileoverview Public consent landing page.
 *
 * Opened from the consent request email by a parent (minors) or the student
 * themselves (adults). Renders the plain-language consent text served by the
 * backend (so the logged consent_text_version matches what was displayed),
 * the COPPA direct notice for under-13 students, and an optional
 * "CC the teen" toggle for 13-17.
 *
 * The GET is a pure read — granting/declining only happens on an explicit
 * button press (POST), so email link-prefetchers can't consent by accident.
 */

import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, ShieldX, Loader2, CheckCircle2, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";
import { getErrorMessage } from "@/lib/api";

const PAGE_BG = {
  background:
    "radial-gradient(ellipse at 50% 0%, rgba(244,207,8,0.055) 0%, rgba(244,207,8,0.015) 40%, transparent 70%) no-repeat, #09090b",
};

const Header = () => (
  <div className="text-center mb-8">
    <div className="flex items-center justify-center gap-3 mb-6">
      <img src="/logo192.png" alt="Hans" className="h-10 rounded-lg" />
      <span
        className="text-lg font-semibold text-white tracking-tight"
        style={{ fontFamily: "Outfit, sans-serif" }}
      >
        Hans
      </span>
    </div>
  </div>
);

const ConsentPage = () => {
  const { token } = useParams();

  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  // Defaults ON — the practice notes are written for the student, so a teen is
  // copied unless the parent opts out here.
  const [ccTeen, setCcTeen] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // "granted" | "declined"

  useEffect(() => {
    const controller = new AbortController();
    const load = async () => {
      try {
        const res = await axios.get(`${API}/consent/${token}`, { signal: controller.signal });
        if (res.data.status && res.data.status !== "requested") {
          setResult(res.data.status);
        }
        setInfo(res.data);
      } catch (err) {
        if (controller.signal.aborted) return;
        setError(err.response?.data?.detail || "This consent link is not valid.");
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    load();
    return () => controller.abort();
  }, [token]);

  const handleAccept = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/consent/${token}/accept`, { cc_teen: ccTeen });
      setResult("granted");
    } catch (err) {
      toast.error(getErrorMessage(err, "Could not record your consent — please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecline = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/consent/${token}/decline`);
      setResult("declined");
    } catch (err) {
      toast.error(getErrorMessage(err, "Something went wrong — please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={PAGE_BG}
        data-theme="dark"
      >
        <Loader2 className="w-8 h-8 text-[#f4cf08] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="min-h-screen flex items-center justify-center text-center p-8"
        style={PAGE_BG}
        data-theme="dark"
      >
        <div>
          <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <ShieldX className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="text-xl font-light text-white mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
            Link Not Valid
          </h1>
          <p className="text-zinc-400 text-sm max-w-sm">{error}</p>
          <p className="text-zinc-500 text-xs mt-4">
            If you were expecting a consent request, ask the instructor to send a new one.
          </p>
        </div>
      </div>
    );
  }

  if (result === "granted") {
    return (
      <div
        className="min-h-screen flex items-center justify-center text-center p-8"
        style={PAGE_BG}
        data-theme="dark"
      >
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <div className="w-16 h-16 bg-[#f4cf08]/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-8 h-8 text-[#f4cf08]" />
          </div>
          <h1 className="text-xl font-light text-white mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
            Consent recorded — thank you!
          </h1>
          <p className="text-zinc-400 text-sm max-w-md mx-auto">
            Lessons can now be recorded and you'll receive a summary after each one. Every summary
            email includes a link to withdraw this consent at any time.
          </p>
          {info?.age_band === "under_13" && (
            <p className="text-zinc-500 text-xs mt-4 max-w-md mx-auto flex items-center justify-center gap-2">
              <Mail className="w-3.5 h-3.5" />
              A confirmation email is on its way. If you didn't give this consent, it contains a
              link to cancel.
            </p>
          )}
        </motion.div>
      </div>
    );
  }

  if (result === "declined") {
    return (
      <div
        className="min-h-screen flex items-center justify-center text-center p-8"
        style={PAGE_BG}
        data-theme="dark"
      >
        <div>
          <h1 className="text-xl font-light text-white mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
            No problem — nothing will be recorded.
          </h1>
          <p className="text-zinc-400 text-sm max-w-md mx-auto">
            We've let the instructor know. No lessons will be recorded, and no information will be
            collected. If you change your mind, the instructor can send a new request.
          </p>
        </div>
      </div>
    );
  }

  if (result === "revoked") {
    return (
      <div
        className="min-h-screen flex items-center justify-center text-center p-8"
        style={PAGE_BG}
        data-theme="dark"
      >
        <div>
          <h1 className="text-xl font-light text-white mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
            Consent was withdrawn
          </h1>
          <p className="text-zinc-400 text-sm max-w-md mx-auto">
            Recording is locked for this student. Ask the instructor to send a new request if you'd
            like to re-enable it.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 py-12"
      style={PAGE_BG}
      data-theme="dark"
    >
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-lg">
        <Header />

        <div className="text-center mb-6">
          <div className="w-14 h-14 bg-[#f4cf08]/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <ShieldCheck className="w-7 h-7 text-[#f4cf08]" />
          </div>
          <h1 className="text-2xl font-light text-white mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
            Lesson recording consent
          </h1>
          <p className="text-zinc-400 text-sm">
            <span className="text-white font-medium">{info.teacher_name}</span> is requesting
            consent for <span className="text-white font-medium">{info.student_name}</span>'s music
            lessons.
          </p>
        </div>

        {/* Consent text — served by the backend so the logged version matches */}
        <div className="bg-zinc-900/50 border border-white/5 rounded-2xl p-6 mb-4">
          <p className="text-zinc-300 text-sm leading-relaxed whitespace-pre-line">
            {info.consent_text}
          </p>
        </div>

        {/* COPPA direct notice (under-13 only) */}
        {info.coppa_direct_notice && (
          <div className="bg-zinc-900/50 border border-[#f4cf08]/20 rounded-2xl p-6 mb-4">
            <p className="text-zinc-400 text-xs leading-relaxed whitespace-pre-line">
              {info.coppa_direct_notice}
            </p>
          </div>
        )}

        {/* CC-the-teen toggle (13-17 only) */}
        {info.allow_cc_teen && (
          <label className="flex items-start gap-3 bg-zinc-900/50 border border-white/5 rounded-2xl p-4 mb-4 cursor-pointer">
            <input
              type="checkbox"
              checked={ccTeen}
              onChange={(e) => setCcTeen(e.target.checked)}
              className="mt-0.5 accent-[#f4cf08]"
              data-testid="cc-teen-toggle"
            />
            <span className="text-zinc-300 text-sm">
              Also send lesson summaries to {info.student_first_name} directly — the practice notes
              are written for them.
            </span>
          </label>
        )}

        <Button
          onClick={handleAccept}
          disabled={submitting}
          className="w-full bg-[#f4cf08] text-black font-medium rounded-full py-3 hover:scale-105 active:scale-95 transition-transform mb-3"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
          I agree
        </Button>
        <button
          type="button"
          onClick={handleDecline}
          disabled={submitting}
          className="w-full text-zinc-500 text-sm hover:text-zinc-300 transition-colors py-2"
        >
          No, I don't consent
        </button>

        <p className="text-center text-zinc-600 text-[11px] mt-6">
          Consent text version {info.consent_text_version}. Questions? Contact{" "}
          <a href={`mailto:${info.support_email}`} className="text-zinc-500 hover:underline">
            {info.support_email}
          </a>
        </p>
      </motion.div>
    </div>
  );
};

export default ConsentPage;
